import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Upload, Briefcase, GraduationCap, Shield, ArrowRight, ChevronDown } from 'lucide-react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useAuth } from '../auth/AuthComponents';
import HeroParticles from './HeroParticles';

const ModernHomepage = ({ onGetStarted }) => {
  const { isAuthenticated } = useAuth();
  const { setShowAuthModal } = useOutletContext();
  const [selectedDocType, setSelectedDocType] = useState('');
  const [currentText, setCurrentText] = useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [formData, setFormData] = useState({
    jobRole: '',
    jobDescription: '',
    programType: '',
    institution: '',
    courseOfStudy: '',
    yearsOfExperience: '',
    file: null
  });
  const navigate = useNavigate();

  const animatedTexts = [
    'Contract',
    'CV & SOP',
  ];

  const analysisOptions = [
    {
      id: 'cv',
      title: 'CVs',
      icon: <Briefcase className="w-5 h-5 text-[var(--color-primary)]" />,
    },
    {
      id: 'sop',
      title: 'SOPs',
      icon: <GraduationCap className="w-5 h-5 text-[var(--color-accent)]" />,
    },
    {
      id: 'contract',
      title: 'Contracts',
      icon: <Shield className="w-5 h-5 text-[var(--color-primary)]" />,
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentText((prev) => (prev + 1) % animatedTexts.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    setFormData(prev => ({ ...prev, file }));
  };

  const handleAnalyzeDocument = () => {
    onGetStarted();
  };

  const inputClass =
    'w-full px-3 sm:px-4 py-2.5 sm:py-3 public-input-elevated rounded-xl sm:rounded-2xl text-[var(--color-text)] placeholder-[var(--color-placeholder)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] focus:border-[var(--color-primary)] transition-all text-sm sm:text-base';

  const renderDocumentForm = () => {
    const selectedDoc = analysisOptions.find(doc => doc.id === selectedDocType);
    if (!selectedDoc) return null;

    return (
      <div className="max-w-2xl mx-auto animate-slideUp">
        <div className="text-center mb-4 sm:mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)] mb-3 sm:mb-4">
            <div className="text-[var(--color-text-inverse)]">
              {selectedDoc.icon}
            </div>
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-[var(--color-text)] mb-2">{selectedDoc.title}</h3>
        </div>

        <div className="elevated-card backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-6 space-y-3 sm:space-y-4">
          {selectedDocType === 'cv' && (
            <>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-[var(--color-text-secondary)] mb-1 sm:mb-2">Job Role</label>
                <input
                  type="text"
                  value={formData.jobRole}
                  onChange={(e) => handleInputChange('jobRole', e.target.value)}
                  placeholder="e.g., Senior Software Engineer"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-[var(--color-text-secondary)] mb-1 sm:mb-2">Job Description</label>
                <textarea
                  value={formData.jobDescription}
                  onChange={(e) => handleInputChange('jobDescription', e.target.value)}
                  placeholder="Paste the job description here..."
                  rows={3}
                  className={`${inputClass} resize-none`}
                />
              </div>
            </>
          )}

          {selectedDocType === 'sop' && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-[var(--color-text-secondary)] mb-1 sm:mb-2">Program Type</label>
                  <input
                    type="text"
                    value={formData.programType}
                    onChange={(e) => handleInputChange('programType', e.target.value)}
                    placeholder="e.g., Master's, PhD"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-[var(--color-text-secondary)] mb-1 sm:mb-2">Institution</label>
                  <input
                    type="text"
                    value={formData.institution}
                    onChange={(e) => handleInputChange('institution', e.target.value)}
                    placeholder="University name"
                    className={inputClass}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-[var(--color-text-secondary)] mb-1 sm:mb-2">Course of Study</label>
                  <input
                    type="text"
                    value={formData.courseOfStudy}
                    onChange={(e) => handleInputChange('courseOfStudy', e.target.value)}
                    placeholder="e.g., Computer Science"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-[var(--color-text-secondary)] mb-1 sm:mb-2">Years of Experience</label>
                  <input
                    type="number"
                    value={formData.yearsOfExperience}
                    onChange={(e) => handleInputChange('yearsOfExperience', e.target.value)}
                    placeholder="e.g., 3"
                    className={inputClass}
                  />
                </div>
              </div>
            </>
          )}

          <div>
            <label className="block text-xs sm:text-sm font-medium text-[var(--color-text-secondary)] mb-1 sm:mb-2">Upload Document</label>
            <div className="relative">
              <input
                type="file"
                onChange={handleFileUpload}
                accept=".pdf,.doc,.docx,.txt"
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="flex items-center justify-center w-full px-3 sm:px-4 py-4 sm:py-6 border-2 border-dashed border-[var(--color-border)] rounded-xl sm:rounded-2xl cursor-pointer hover:border-[var(--color-primary)] transition-all group"
              >
                <div className="text-center">
                  <Upload className="w-6 h-6 sm:w-8 sm:h-8 text-[var(--color-text-muted)] group-hover:text-[var(--color-primary)] mx-auto mb-2 transition-colors" />
                  <p className="text-[var(--color-text-muted)] group-hover:text-[var(--color-text)] transition-colors text-xs sm:text-sm">
                    {formData.file ? (
                      <span className="font-medium">{formData.file.name}</span>
                    ) : (
                      <>
                        <span className="hidden sm:inline">Click to upload or drag and drop</span>
                        <span className="sm:hidden">Tap to select file</span>
                      </>
                    )}
                  </p>
                  <p className="text-xs text-[var(--color-text-muted)] mt-1">PDF, DOC, DOCX or TXT files</p>
                </div>
              </label>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              onClick={() => setSelectedDocType('')}
              className="w-full sm:flex-1 bg-[var(--color-surface-muted)] hover:opacity-90 text-[var(--color-text-secondary)] px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl sm:rounded-2xl font-medium transition-all text-sm sm:text-base"
            >
              Back
            </button>
            <button
              onClick={handleAnalyzeDocument}
              className="w-full sm:flex-2 brand-gradient-btn px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl sm:rounded-2xl transition-all transform hover:scale-105 flex items-center justify-center group text-sm sm:text-base"
            >
              Analyze Document
              <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes float {
        0%, 100% { transform: translate(0, 0) rotate(0deg); }
        33% { transform: translate(10px, -15px) rotate(1deg); }
        66% { transform: translate(-5px, 5px) rotate(-1deg); }
      }
      @keyframes float2 {
        0%, 100% { transform: translate(0, 0) rotate(0deg); }
        50% { transform: translate(-20px, -20px) rotate(2deg); }
      }
      @keyframes slideUp {
        from { opacity: 0; transform: translateY(30px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes slideUpFromBottom {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes heroTextEnter {
        from { opacity: 0; transform: translateY(14px); filter: blur(4px); }
        to { opacity: 1; transform: translateY(0); filter: blur(0); }
      }
      .animate-float { animation: float 8s ease-in-out infinite; }
      .animate-float2 { animation: float2 10s ease-in-out infinite; }
      .animate-slideUp { animation: slideUp 0.6s ease-out; }
      .animate-slideUpFromBottom { animation: slideUpFromBottom 0.4s ease-out; }
      .animate-hero-text { animation: heroTextEnter 0.55s ease-out both; }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  return (
    <div className="flex-1 flex flex-col public-page-glow relative overflow-hidden min-h-0">
      <Helmet>
        <title>DocxsAi – AI Document Analysis Tool</title>
        <meta name="description" content="Generate and analyze Contracts, CVs, and SOPs with AI. Professional templates, detailed legal drafting, and instant downloads." />
        <link rel="canonical" href="https://docxsai.com/" />
        <meta property="og:title" content="DocxsAi – AI Document Analysis Tool" />
        <meta property="og:description" content="Optimize Contracts, Resumes & SOPs Instantly with AI." />
        <meta property="og:url" content="https://docxsai.com/" />
        <meta property="og:image" content="https://ik.imagekit.io/tabsoltech/General/DocxsAi-Preview.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context":"https://schema.org",
          "@type":"Organization",
          name:"DocxsAi",
          url:"https://docxsai.com",
          logo:"https://ik.imagekit.io/gr1ybccbs/DocxsAi-light.png",
          sameAs:["https://www.instagram.com/docxsai?utm_source=qr&igsh=YTdjMnpqajY1eXJ1"],
          contactPoint:{"@type":"ContactPoint","contactType":"customer support","email":"info@docxsai.com","telephone":"08132888981","areaServed":"NG","availableLanguage":["en"]}
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context":"https://schema.org",
          "@type":"WebSite",
          url:"https://docxsai.com",
          potentialAction:{
            "@type":"SearchAction",
            target:"https://docxsai.com/?q={search_term_string}",
            "query-input":"required name=search_term_string"
          }
        })}</script>
      </Helmet>
      {/* Soft brand glows — light mode stays light; dark mode uses primary-soft via tokens */}
      <div className="absolute top-10 left-4 w-32 h-32 sm:top-20 sm:left-20 sm:w-80 sm:h-80 bg-[var(--color-primary-soft)] rounded-full blur-3xl animate-float pointer-events-none z-[1]"></div>
      <div className="absolute top-20 right-4 w-40 h-40 sm:top-40 sm:right-20 sm:w-96 sm:h-96 bg-gradient-to-br from-[var(--color-gradient-from)]/10 to-[var(--color-gradient-to)]/10 rounded-full blur-3xl animate-float2 pointer-events-none z-[1]"></div>
      <div className="absolute bottom-16 left-6 w-24 h-24 sm:bottom-32 sm:left-32 sm:w-64 sm:h-64 bg-[var(--color-primary-soft)] rounded-full blur-3xl animate-float pointer-events-none z-[1]"></div>
      <div className="absolute bottom-10 right-6 w-28 h-28 sm:bottom-20 sm:right-40 sm:w-72 sm:h-72 bg-gradient-to-br from-[var(--color-gradient-to)]/10 to-[var(--color-gradient-from)]/10 rounded-full blur-3xl animate-float2 pointer-events-none z-[1]"></div>
      <HeroParticles />

      <main className="relative z-10 flex-1 flex items-center justify-center px-4 sm:px-6 pt-20 sm:pt-24 pb-6 sm:pb-8">
        <div className="max-w-6xl mx-auto text-center w-full">
          {!selectedDocType ? (
            <>
              <h1 className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[var(--color-text)] mb-4 sm:mb-6 leading-tight px-2">
                AI-Powered Document Intelligence.
                <br className="hidden sm:block" />
                <span className="sm:hidden"> </span>
                <span
                  key={currentText}
                  className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)] animate-hero-text inline-block"
                >
                  {animatedTexts[currentText]}
                </span>
                {" "}Analysis
              </h1>

              <p className="text-sm sm:text-lg md:text-xl text-[var(--color-text-muted)] mb-8 sm:mb-12 max-w-3xl mx-auto leading-relaxed px-4">
                  Analyze CVs, Contracts, and SOPs leveraging AI. Spot risks, get clear recommendations, revamp or generate documents in minutes.
              </p>

              <div className="max-w-4xl mx-auto px-2" ref={dropdownRef}>
                <div className="relative">
                    <button
                      type="button"
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className={`hero-analyze-trigger flex-grow w-full px-4 sm:px-6 py-3 sm:py-4 public-input-elevated rounded-xl sm:rounded-2xl text-[var(--color-text-muted)] text-sm sm:text-lg cursor-pointer flex justify-between items-center text-left ${isDropdownOpen ? 'hero-analyze-trigger--open' : ''}`}
                      aria-expanded={isDropdownOpen}
                      aria-haspopup="listbox"
                    >
                      <span>What document would you like to process today?</span>
                      <span className="hero-analyze-chevron flex items-center justify-center shrink-0 ml-3">
                        <ChevronDown className={`w-5 h-5 text-[var(--color-primary)] transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                      </span>
                    </button>

                  {isDropdownOpen && (
                    <div className="absolute top-full mt-2 w-full elevated-card backdrop-blur-sm rounded-2xl p-4 z-20 animate-slideUpFromBottom">
                      <div className="text-left text-xs text-[var(--color-text-muted)] px-3 pb-2">Available options</div>
                      <div className="flex justify-around items-center">
                        {analysisOptions.map((option) => (
                          <div
                            key={option.id}
                            onClick={() => {
                              if (!isAuthenticated) { setShowAuthModal(true); setIsDropdownOpen(false); return; }
                              navigate(`/document-analysis?type=${option.id}`); setIsDropdownOpen(false);
                            }}
                            className="flex items-center space-x-2 p-3 rounded-lg hover:bg-[var(--color-surface-muted)] hover:scale-[1.03] cursor-pointer transition-all duration-200"
                          >
                            {option.icon}
                            <div className="text-[var(--color-text)] font-medium text-sm">{option.title}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            renderDocumentForm()
          )}
        </div>
      </main>
    </div>
  );
};

export default ModernHomepage;
